// Countdown shown in the last minute of the HIPAA automatic-logoff window, so
// nobody is dumped to /Login mid-read without warning. See hooks/useIdleLogout.
//
// Deliberately modal and focus-grabbing: the point is that a person actively
// confirms they're still there. Ordinary mouse movement does NOT dismiss it.

import React, { useEffect, useRef } from 'react';

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 100000,
  background: 'rgba(16,38,40,0.55)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
};
const panel: React.CSSProperties = {
  background: 'var(--rr-paper, #fff)', color: 'var(--pm-text, #1a1a1a)',
  border: '1px solid var(--pm-border, #d8d4cc)', borderRadius: 14,
  padding: '22px 24px', maxWidth: 420, width: '100%',
  boxShadow: '0 12px 40px rgba(0,0,0,0.25)', colorScheme: 'light',
};
const primaryBtn: React.CSSProperties = {
  background: 'var(--rr-deep-700, #20454A)', color: '#fff', border: 'none',
  borderRadius: 8, padding: '10px 18px', fontSize: 14, fontWeight: 700, cursor: 'pointer',
};
const secondaryBtn: React.CSSProperties = {
  background: 'var(--pm-bg-input, #fff)', color: 'var(--pm-text, #1a1a1a)',
  border: '1px solid var(--pm-border, #d8d4cc)', borderRadius: 8,
  padding: '10px 16px', fontSize: 14, cursor: 'pointer',
};

const IdleWarning: React.FC<{
  secondsLeft: number;
  onStay: () => void;
  onSignOut: () => void;
}> = ({ secondsLeft, onStay, onSignOut }) => {
  const stayRef = useRef<HTMLButtonElement | null>(null);

  // Focus the primary action so Enter/Space keeps you signed in.
  useEffect(() => { stayRef.current?.focus(); }, []);

  return (
    <div style={overlay} role="alertdialog" aria-modal="true" aria-labelledby="idle-title" aria-describedby="idle-desc">
      <div style={panel}>
        <h2 id="idle-title" style={{ margin: '0 0 8px', fontSize: 18 }}>Still there?</h2>
        <p id="idle-desc" style={{ margin: '0 0 4px', fontSize: 14, lineHeight: 1.5, color: '#555' }}>
          You’ll be signed out in{' '}
          <b style={{ color: 'var(--rr-red, #C2433D)' }} aria-live="assertive">
            {secondsLeft} second{secondsLeft === 1 ? '' : 's'}
          </b>{' '}
          because of inactivity.
        </p>
        <p style={{ margin: '0 0 18px', fontSize: 12, color: '#888', lineHeight: 1.5 }}>
          Automatic sign-out protects patient information on unattended devices. It applies to every
          session, including when “Keep me signed in” is checked.
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button type="button" style={secondaryBtn} onClick={onSignOut}>Sign out now</button>
          <button type="button" style={primaryBtn} onClick={onStay} ref={stayRef}>Stay signed in</button>
        </div>
      </div>
    </div>
  );
};

export default IdleWarning;
